import React from 'react'
import './index.scss'
import Layout from '../../components/BridgePage/Layout'

const AboutTs = () => (
  <Layout>
    <div className="tsContainer">
      <h1 className="tsTitle">Huna User Agreement</h1>

      <p><strong>Effective Date:</strong> January 20, 2025</p>

      <h2>Section 1: Introduction and Agreement Acceptance</h2>
      <p>
        Huna welcomes you to its platform, comprising websites, mobile applications, and related services or products. These terms regulate your use of Huna’s offerings and your interactions with the platform.
        By utilizing Huna’s services, you agree to abide by the rules set forth herein, as well as any additional guidelines or conditions relevant to specific features or paid services. These supplementary provisions are integral to this agreement. Should you disagree with any part of these terms or Huna’s privacy policy, you must discontinue your use of the platform immediately.
        While accessing Huna’s services, you may encounter content generated by third parties or users. Huna disclaims responsibility for the accuracy, suitability, or legality of such content. Furthermore, you accept the risk of encountering offensive or erroneous material and waive any claims against Huna arising from exposure to such content.
        Huna’s services are not available to individuals or entities located in restricted regions, including Cuba, Iran, North Korea, Sudan, Syria, the Crimean region, or areas designated as DNR or LNR. Users in these areas are prohibited from accessing the platform.
        We retain the right to update these terms periodically, for instance, when new features are introduced, services are consolidated, or legal requirements change. Notices of significant amendments will be published on the platform, and the “Effective Date” will indicate the most recent revisions. Your continued use of Huna confirms your acceptance of the modified terms.
        When engaging with the platform, you are required to adhere to Huna’s Privacy Policy, Community Guidelines, and Child Safety Policy. Additional rules may apply to specific features or services, and such terms will be presented in conjunction with those offerings. For paid services, extra terms will also be disclosed during the purchase process.
        The service is not available to anyone under the age of 18. By using the service, you agree that:
        ● You must be at least the "Minimum Age" (as defined below) or older.
        ● Huna has not restricted your access to the service. Creating an account with false information, including accounts registered on behalf of someone else or individuals under the age of 18, violates our terms.
        "Minimum Age" means 18 years old. However, if the laws of your jurisdiction require that you must be older than 18 for Huna to lawfully provide the service to you without parental consent (including using your personal data), then the Minimum Age is the age specified by your jurisdiction.
      </p>

      <h2>Section 2: Your Responsibilities and Prohibited Activities</h2>
      <p>
        By accessing or using Huna’s platform, you agree to act in a manner that aligns with legal and ethical standards. The following activities are strictly prohibited:
        ● Unauthorized access to other users’ accounts or Huna’s systems.
        ● Uploading harmful or malicious content, including software.
        ● Disseminating defamatory, obscene, or discriminatory material.
        ● Participating in harassment, fraudulent actions, or illegal activities.
        Huna reserves the right to suspend accounts, restrict access, or pursue legal action against users who violate these terms or engage in harmful behavior.
      </p>

      <h2>Section 3: Payments and Billing Policies</h2>
      <p>
        Huna accepts major credit cards, select debit cards, and other payment methods as may be introduced. By making a purchase, you authorize Huna or its payment processors to charge your account for the full amount of the purchase.
        You may be required to provide personal details (such as government-issued identification) to process payments and comply with relevant legal requirements. If your payment is declined, verify your information and try again. For further assistance, contact our support team.
      </p>

      <h2>Section 4: User Accounts and Security</h2>
      <p>
        To access certain features, you must create an account with accurate and up-to-date information. Using offensive or inappropriate usernames is prohibited and may result in account termination.
        You are responsible for securing your account and notifying Huna of any unauthorized access. If you wish to delete your account, you may contact us at [report@huna.livem]. Account deletion is permanent, and your data will not be recoverable.
      </p>
      <h2>Section 5: Guidelines for User-Generated Content (UGC)</h2>
      <p>
        By submitting any user-generated content (UGC) through Huna’s platform, you grant Huna and its sublicensees an irrevocable, perpetual, and non-exclusive license to use, modify, distribute, and publicly display such content. This license is royalty-free, worldwide, and includes the right to create derivative works.
      </p>
      <p>Additionally, you agree to the following:</p>
      <ul>
        <li>
          <strong>Content Legality and Copyright:</strong> Your UGC must not infringe on any third party’s copyright, trademark, or other intellectual property rights. If you include protected material, you must have explicit permission or legal authority to use it.
        </li>
        <li>
          <strong>Prohibited Content:</strong> UGC must not contain illegal, defamatory, obscene, violent, or hateful material, nor promote unlawful activities or endanger public safety.
        </li>
        <li>
          <strong>Accuracy of Content:</strong> You are responsible for ensuring the accuracy and truthfulness of the content you provide. Misleading or false information is strictly prohibited.
        </li>
        <li>
          <strong>Maintaining Platform Integrity:</strong> UGC that harms Huna’s reputation or compromises the safety and integrity of the platform is prohibited. Huna reserves the right to remove or restrict such content.
        </li>
      </ul>
      <p>
        You are solely responsible for the content you submit and the consequences of sharing it. Huna disclaims any liability for user-generated content but reserves the right to review, remove, or restrict content that violates these terms.
      </p>
      <p>
        While Huna does not guarantee the monitoring of all UGC, we may take the following actions if violations are identified:
      </p>
      <ul>
        <li>Remove or restrict access to the content in question.</li>
        <li>Suspend or terminate accounts associated with the violations.</li>
        <li>Report illegal or harmful activities to the relevant authorities.</li>
      </ul>
      <p>
        By submitting UGC, you acknowledge that Huna may use the content for promotional purposes, technical testing, or other lawful activities without additional notification or compensation.
      </p>

      <h2>Section 6: Third-Party Services and Mobile Access</h2>
      <p>
        When using third-party services through Huna’s platform, you agree to their respective terms and privacy policies. Huna is not responsible for third-party services. Mobile access may incur additional carrier charges, which are your responsibility.
      </p>

      <h2>Section 7: Content Moderation and Enforcement</h2>
      <p>
        To maintain a safe and respectful environment, Huna reserves the right to:
      </p>
      <ul>
        <li>Moderate or remove content that violates these terms.</li>
        <li>Work with law enforcement to address illegal activities.</li>
        <li>Limit or suspend access to the platform if terms are breached.</li>
      </ul>
      <p>
        While Huna strives to monitor user activity, we do not guarantee the immediate removal of all harmful or inappropriate content.
      </p>

      <h2>Section 8: Intellectual Property Rights</h2>
      <p>
        All trademarks, logos, and proprietary content on the Huna platform remain the property of Huna. You are granted a limited license to access the platform for personal use. Unauthorized reproduction, modification, or distribution is strictly prohibited.
      </p>

      <h2>Section 9: Disclaimer of Warranties and Limitation of Liability</h2>
      <p>
        Huna’s services are provided “as is” without warranties of any kind. To the fullest extent permitted by law, Huna disclaims all liability for damages, including lost profits or data. Huna’s total liability is limited to the amount paid by you for the services, if any.
      </p>

      <h2>Section 10: Indemnification</h2>
      <p>
        You agree to indemnify and hold Huna harmless from any claims, damages, or legal fees arising from your use of the platform, violation of these terms, or infringement of third-party rights.
      </p>

      <h2>Section 11: Governing Law and Dispute Resolution</h2>
      <p>
        These terms are governed by the laws of Singapore. Disputes will first be addressed through friendly negotiation. If unresolved within 30 days, disputes will be referred to the Singapore International Arbitration Centre (SIAC), with proceedings conducted in English. The arbitration award will be final and binding.
      </p>

      <h2>Section 12: General Provisions</h2>
      <p>
        Failure to enforce any provision of these terms does not constitute a waiver. If any term is deemed unenforceable, the remaining terms remain valid. These terms represent the entire agreement between you and Huna regarding your use of the platform.
      </p>
      <p>For inquiries or feedback, contact us at <a href="">report@huna.live</a>.</p>

    </div>
  </Layout>
)

export default AboutTs
